import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminState {
  /** True while the session + RPC check is in flight. */
  loading: boolean;
  /** True only for a backoffice admin (DB-enforced: is_admin flag AND @flathero.pl). */
  isAdmin: boolean;
  /** Whether there's an authenticated session at all — lets the gate tell apart
   * "log in first" (redirect) from "logged in but not an admin" (no-access). */
  hasSession: boolean;
}

/**
 * UI gate for the backoffice. The DB is the source of truth: flat.is_admin()
 * returns true only for an is_admin user on an @flathero.pl email, and the
 * analytics RPCs / demand views enforce it server-side regardless of this hook.
 * Any RPC error is treated as not-admin (fail closed).
 */
export function useIsAdmin(): AdminState {
  const [state, setState] = useState<AdminState>({
    loading: true,
    isAdmin: false,
    hasSession: false,
  });

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        if (!cancelled) setState({ loading: false, isAdmin: false, hasSession: false });
        return;
      }
      const { data, error } = await supabase.schema('flat' as never).rpc('is_admin' as never);
      if (!cancelled) {
        setState({ loading: false, isAdmin: !error && Boolean(data), hasSession: true });
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
